import {StepperComponent} from '../../../../_metronic/assets/ts/components'
import {useIntl} from 'react-intl'
import {useEffect, useRef, useState} from 'react'
import {Form, Formik} from 'formik'
import {toast} from 'react-toastify'
import {useAuth} from '../../auth'
import {createDraft, getSingleTemplate, publishTemplate, updateTemplate} from '../core/_request'
import {ComposeEmail} from './TemplateSteps/ComposeEmail'
import {Preview} from './TemplateSteps/Preview'
import * as Yup from 'yup'
import {useNavigate, useParams} from 'react-router-dom'
import {AddInvestorUpdateModal} from '../views/AddInvestorUpdateModal'
import {AddInsertPitchDeskModal} from '../views/AddInsertPitchDeskModal'
import TagManager from 'react-gtm-module'
import {GTMEvent} from '../../../hooks/gtmHelpers'

const CreateTemplate = () => {
  const stepperRef = useRef<HTMLDivElement | null>(null)
  const stepper = useRef<StepperComponent | null>(null)
  const navigate = useNavigate()
  const {formatMessage} = useIntl()

  const [quillText, setQuillText] = useState('')
  const [createdTemplateId, setCreatedTemplateId] = useState<any>()
  const [emailSignature, setEmailSignature] = useState('')
  const [footer, setFooter] = useState(false)
  const [templateData, setTemplateData] = useState<any>()
  const {companyId, currentUser} = useAuth()
  const [addInvestorUpdateModalShow, setAddInvestorUpdateModalShow] = useState(false)
  const [storeFileIds, setStoreFileIds] = useState<any>([])
  const [saveTemplateLoading, setSaveTemplateLoading] = useState(false)
  const [draftTemplateLoading, setDraftTemplateLoading] = useState(false)
  const [addInsertPitchDeskModalShow, setAddInsertPitchDeskModalShow] = useState(false)
  const [previewTemplateId, setPreviewTemplateId] = useState<any>()
  const {templateId} = useParams()
  const [, setsteps] = useState<any>(stepper.current?.currentStepIndex)

  const initialValues: any = {
    name: '',
    subject: '',
    quillText: '',
    files: [],
    pitchDeckId: '',
    updateId: '',
  }
  const composeEmailSchema = Yup.object().shape({
    name: Yup.string()
      .min(1, formatMessage({id: 'Minimum 1 character is required'}))
      .max(50, formatMessage({id: 'Maximum 50 characters'}))
      .required(formatMessage({id: 'Name is required'}))
      .nullable(),
    subject: Yup.string()
      .required(formatMessage({id: 'Subject is required'}))
      .nullable(),
    quillText: Yup.string()
      .required(formatMessage({id: 'Email body is required'}))
      .nullable(),
    pitchDeckId: Yup.string().nullable(),
    updateId: Yup.string().nullable(),
    files: Yup.array().of(Yup.number()),
  })

  const addFiles = storeFileIds?.map((file: any) => file.fileId)

  const loadStepper = () => {
    stepper.current = StepperComponent.createInsance(stepperRef.current as HTMLDivElement)
  }

  const prevStep = () => {
    if (!stepper.current) {
      return
    }

    stepper.current.goPrev()
    setsteps(stepper.current?.currentStepIndex)
  }

  const submitStep = () => {}

  useEffect(() => {
    if (!stepperRef.current) {
      return
    }
    loadStepper()
  }, [stepperRef])

  const saveAsDraft = async (formik: any) => {
    try {
      setDraftTemplateLoading(true)
      if (companyId) {
        let payload: any
        if (footer) {
          payload = {
            name: formik.values.name,
            subject: formik.values.subject,
            body: formik.values.quillText,
            files: addFiles,
            footer: emailSignature,
          }
        } else {
          payload = {
            name: formik.values.name,
            subject: formik.values.subject,
            body: formik.values.quillText,
            files: addFiles,
            footer: null,
          }
        }

        if (formik?.values?.pitchDeckId) {
          payload.pitchDeckId = parseInt(formik.values.pitchDeckId)
        }

        if (formik?.values?.updateId) {
          payload.updateId = parseInt(formik?.values?.updateId)
        }

        if (templateId || createdTemplateId) {
          const {
            data: {success, errors},
          }: any = await updateTemplate(payload, templateId || createdTemplateId)

          if (success) {
            setDraftTemplateLoading(false)
            toast.success(formatMessage({id: 'Template updated successfully.'}))
            stepper?.current?.goNext()
            setPreviewTemplateId(templateId)
            if (previewTemplateId) {
              getSingleTemplateData(previewTemplateId)
            }
            TagManager.dataLayer({
              dataLayer: {
                event: GTMEvent.UpdateTemplateSuccessful,
                action: 'normal',
                metadata: {
                  user_id: currentUser?.userId,
                  email: currentUser?.email,
                  company_id: companyId,
                  template_id: templateId || createdTemplateId,
                  template_name: formik.values.name,
                  template_subject: formik.values.subject,
                },
              },
            })
          } else {
            setDraftTemplateLoading(false)
            errors.forEach((error: string) => {
              toast.error(formatMessage({id: error}))
            })
            TagManager.dataLayer({
              dataLayer: {
                event: GTMEvent.UpdateTemplateFailed,
                action: 'normal',
                metadata: {
                  user_id: currentUser?.userId,
                  email: currentUser?.email,
                  company_id: companyId,
                  templateId: templateId || createdTemplateId,
                  template_name: formik.values.name,
                  template_subject: formik.values.subject,
                  error: errors[0],
                },
              },
            })
          }
        } else {
          const {
            data: {data, success, errors},
          }: any = await createDraft(payload, companyId)

          if (success) {
            setDraftTemplateLoading(false)
            toast.success(formatMessage({id: 'Template saved as Draft.'}))
            stepper?.current?.goNext()
            setCreatedTemplateId(data.templateId)
            setPreviewTemplateId(data.templateId)
            if (previewTemplateId) {
              getSingleTemplateData(previewTemplateId)
            }
            TagManager.dataLayer({
              dataLayer: {
                event: GTMEvent.TemplateDraftSavedSuccessful,
                action: 'normal',
                metadata: {
                  user_id: currentUser?.userId,
                  email: currentUser?.email,
                  company_id: companyId,
                  template_id: data.templateId,
                  template_name: formik.values.name,
                  template_subject: formik.values.subject,
                },
              },
            })
          } else {
            setDraftTemplateLoading(false)
            errors.forEach((error: string) => {
              toast.error(formatMessage({id: error}))
            })
            TagManager.dataLayer({
              dataLayer: {
                event: GTMEvent.TemplateDraftSavedFailed,
                action: 'normal',
                metadata: {
                  template_name: formik.values.name,
                  template_subject: formik.values.subject,
                  user_id: currentUser?.userId,
                  email: currentUser?.email,
                  company_id: companyId,
                  error: errors[0],
                },
              },
            })
          }
        }
      }
    } catch (err) {
      setDraftTemplateLoading(false)
      console.log(err)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.UpdateTemplateFailed,
          action: 'normal',
          metadata: {
            user_id: currentUser?.userId,
            email: currentUser?.email,
            company_id: companyId,
            templateId: templateId || createdTemplateId,
            template_name: formik.values.name,
            template_subject: formik.values.subject,
            error: err,
          },
        },
      })
    }
  }

  const handlePublishTemplate = async (formik: any) => {
    if (companyId) {
      setSaveTemplateLoading(true)
      try {
        let payload: any
        if (footer) {
          payload = {
            name: formik.values.name,
            subject: formik.values.subject,
            body: formik.values.quillText,
            footer: emailSignature,
            files: addFiles,
            templateId: createdTemplateId || templateId,
          }
        } else {
          payload = {
            name: formik.values.name,
            subject: formik.values.subject,
            body: formik.values.quillText,
            files: addFiles,
            templateId: createdTemplateId || templateId,
            footer: null,
          }
        }
        if (formik?.values?.pitchDeckId) {
          payload.pitchDeckId = parseInt(formik.values.pitchDeckId)
        }
        if (formik?.values?.updateId) {
          payload.updateId = parseInt(formik?.values?.updateId)
        }
        const {
          data: {success, errors},
        } = await publishTemplate(payload, companyId)

        if (success) {
          toast.success(formatMessage({id: 'Template published successfully.'}))
          setSaveTemplateLoading(false)
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.TemplatePublishedSuccessful,
              action: 'normal',
              metadata: {
                template_name: formik.values.name,
                template_subject: formik.values.subject,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                company_id: companyId,
                template_id: createdTemplateId || templateId,
              },
            },
          })
          navigate('/updates/templates')
        } else {
          setSaveTemplateLoading(false)
          errors.forEach((error: string) => {
            toast.error(formatMessage({id: error}))
          })
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.TemplatePublishedFailed,
              action: 'normal',
              metadata: {
                template_name: formik.values.name,
                template_subject: formik.values.subject,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                company_id: companyId,
                template_id: createdTemplateId || templateId,
                error: errors[0],
              },
            },
          })
        }
      } catch (err) {
        setSaveTemplateLoading(false)
        console.log(err)
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.TemplatePublishedFailed,
            action: 'normal',
            metadata: {
              template_name: formik.values.name,
              template_subject: formik.values.subject,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
              template_id: createdTemplateId || templateId,
              error: err,
            },
          },
        })
      }
    }
  }

  const getSingleTemplateData = async (templateId: any) => {
    try {
      const {
        data: {data, success, errors},
      } = await getSingleTemplate(templateId)
      if (success) {
        initialValues.name = data.name
        initialValues.subject = data.subject
        initialValues.quillText = data.body.replace(/(<([^>]+)>)/gi, '')
        initialValues.updateId = data.updateId
        initialValues.pitchDeckId = data.pitchDeckId
        setTemplateData(data)

        if (!!data.footer) {
          setFooter(true)
          setEmailSignature(data.footer)
        } else {
          setFooter(false)
        }
      } else {
        errors.forEach((error: string) => {
          toast.error(formatMessage({id: error}))
        })
      }
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    if (templateId) {
      getSingleTemplateData(templateId)
    }
  }, [templateId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setsteps(stepper.current?.currentStepIndex)
  }, [stepper.current?.currentStepIndex])

  return (
    <>
      <div
        ref={stepperRef}
        className='stepper stepper-pills stepper-column d-flex flex-column flex-xl-row flex-row-fluid mx-5 font-size-13'
        id='kt_create_account_stepper'
      >
        <div className='card gap-5 w-100 p-4 rounded-0 mt-3'>
          <div className='d-flex flex-row gap-md-20 gap-10 w-100 px-4 mt-5'>
            <div className='stepper-item current' data-kt-stepper-element='nav'>
              <div className='stepper-wrapper d-flex flex-md-row flex-column'>
                <div className='stepper-icon w-40px h-40px ms-md-0 ms-n8'>
                  <i className='stepper-check fas fa-check'></i>
                  <span className='stepper-number'>{formatMessage({id: '1'})}</span>
                </div>
                <div className='stepper-label mt-md-0 mt-2 ms-md-n3 ms-2 text-align-start'>
                  <h3 className='stepper-title font-size-13'>{formatMessage({id: 'Step 1'})}</h3>

                  <div className='stepper-desc fw-semibold font-size-12'>
                    {formatMessage({id: 'Compose Email'})}
                  </div>
                </div>
              </div>
              <div className='stepper-line h-40px'></div>
            </div>
            <div className='stepper-item' data-kt-stepper-element='nav'>
              <div className='stepper-wrapper d-flex flex-md-row flex-column'>
                <div className='stepper-icon w-40px h-40px ms-md-0 ms-n5'>
                  <i className='stepper-check fas fa-check'></i>
                  <span className='stepper-number'>{formatMessage({id: '2'})}</span>
                </div>
                <div className='stepper-label mt-md-0 mt-2 ms-md-n3 ms-2'>
                  <h3 className='font-size-13 stepper-title'>{formatMessage({id: 'Step 2'})}</h3>
                  <div className='stepper-desc fw-semibold font-size-12'>
                    {formatMessage({id: 'Preview'})}
                  </div>
                </div>
              </div>
              <div className='stepper-line h-40px'></div>
            </div>
          </div>
          <div className='d-flex flex-row-fluid bg-body rounded'>
            <Formik
              validationSchema={
                stepper.current?.currentStepIndex === undefined ||
                stepper.current?.currentStepIndex === 1
                  ? composeEmailSchema
                  : ''
              }
              initialValues={initialValues}
              onSubmit={submitStep}
            >
              {(formik) => {
                return (
                  <Form className='py-2 w-100 px-3' noValidate id='kt_create_account_form'>
                    <div className='current' data-kt-stepper-element='content'>
                      <ComposeEmail
                        formik={formik}
                        setQuillText={setQuillText}
                        quillText={quillText}
                        emailSignature={emailSignature}
                        setEmailSignature={setEmailSignature}
                        setFooter={setFooter}
                        footer={footer}
                        setAddInvestorUpdateModalShow={setAddInvestorUpdateModalShow}
                        setStoreFileIds={setStoreFileIds}
                        storeFileIds={storeFileIds}
                        setAddInsertPitchDeskModalShow={setAddInsertPitchDeskModalShow}
                        templateData={templateData}
                      />
                    </div>
                    <div data-kt-stepper-element='content'>
                      <Preview
                        previewTemplateId={previewTemplateId}
                        prevStep={prevStep}
                        templateData={templateData}
                        setTemplateData={setTemplateData}
                      />
                    </div>
                    <div className='d-flex flex-stack pt-10'>
                      <div className='d-flex gap-6 justify-content-end w-100'>
                        <button
                          type='button'
                          className='btn btn-lg btn-light me-3 w-md-auto w-50 border font-size-13 font-weight-400'
                          data-kt-stepper-action='previous'
                          onClick={prevStep}
                        >
                          {formatMessage({id: 'Go Back'})}
                        </button>
                        {stepper.current?.currentStepIndex === 2 && (
                          <button
                            type='button'
                            className='border border-primary btn text-primary w-md-auto w-50 font-size-13 font-weight-400'
                            onClick={() => navigate('/updates/templates')}
                          >
                            {formatMessage({id: 'Discard'})}
                          </button>
                        )}

                        {(stepper.current?.currentStepIndex === undefined ||
                          stepper.current?.currentStepIndex === 1) && (
                          <button
                            type='button'
                            className='border border-primary btn text-primary w-md-auto w-50 font-size-13'
                            onClick={() => navigate('/updates/templates')}
                          >
                            {formatMessage({id: 'Discard'})}
                          </button>
                        )}
                        {(stepper.current?.currentStepIndex === undefined ||
                          stepper.current?.currentStepIndex === 1) && (
                          <button
                            type='button'
                            disabled={
                              !formik.isValid ||
                              draftTemplateLoading ||
                              formik.isSubmitting ||
                              !formik.values.name ||
                              !formik.values.subject
                            }
                            className='btn btn-lg btn-primary w-md-auto w-50 font-size-13'
                            onClick={() => saveAsDraft(formik)}
                          >
                            {!draftTemplateLoading && (
                              <span className='indicator-label font-size-13 p-0'>
                                {formatMessage({id: 'Save draft template'})}
                              </span>
                            )}
                            {draftTemplateLoading && (
                              <span className='indicator-label font-size-13 p-0'>
                                {formatMessage({id: 'Please wait...'})}
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                              </span>
                            )}
                          </button>
                        )}
                        <button
                          disabled={
                            saveTemplateLoading ||
                            !formik.isValid ||
                            draftTemplateLoading ||
                            formik.isSubmitting ||
                            !formik.values.name ||
                            !formik.values.subject
                          }
                          type='button'
                          className='btn btn-primary w-md-auto w-50 font-size-13 font-weight-400'
                          onClick={() => handlePublishTemplate(formik)}
                        >
                          {!saveTemplateLoading && (
                            <span className='indicator-label font-size-13 p-0'>
                              {formatMessage({id: 'Publish Template'})}
                            </span>
                          )}
                          {saveTemplateLoading && (
                            <span className='indicator-label font-size-13 p-0'>
                              {formatMessage({id: 'Please wait...'})}
                              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                    <AddInsertPitchDeskModal
                      addInsertPitchDeskModalShow={addInsertPitchDeskModalShow}
                      setAddInsertPitchDeskModalShow={setAddInsertPitchDeskModalShow}
                      formik={formik}
                    />
                    <AddInvestorUpdateModal
                      addInvestorUpdateModalShow={addInvestorUpdateModalShow}
                      setAddInvestorUpdateModalShow={setAddInvestorUpdateModalShow}
                      formik={formik}
                    />
                  </Form>
                )
              }}
            </Formik>
          </div>
        </div>
      </div>
    </>
  )
}

export default CreateTemplate
